import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { VendaIa } from '@/lib/vendasIa';

export interface VendasIaKpi {
  /** "Hoje" no fuso de São Paulo, dito pelo banco. */
  hoje: string;
  vendas: VendaIa[];
}

/**
 * As vendas registradas pela IsabellA — chip "Vendas IA" do topo e o modal dele.
 * Fonte: RPC `kpi_vendas_ia` (mapa v_isabella_vendas_linhas), mês corrente e
 * anterior. Substituiu o chip de transferências da IA (Douglas 26/09/2026).
 */
export type SegmentoVendasIa = 'PJ' | 'PF';

/** PJ = IsabellA (CNPJ, kpi_vendas_ia) · PF = IA do CPF, Conecta (kpi_vendas_ia_cpf). Nunca somam. */
export function useVendasIaKpi(habilitado: boolean, segmento: SegmentoVendasIa = 'PJ') {
  return useQuery<VendasIaKpi>({
    queryKey: ['vendas-ia-kpi', segmento],
    enabled: habilitado,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: async () => {
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        name: 'kpi_vendas_ia' | 'kpi_vendas_ia_cpf',
      ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
      const { data, error } = await rpc(segmento === 'PF' ? 'kpi_vendas_ia_cpf' : 'kpi_vendas_ia');
      if (error) throw error;
      const bruto = (Array.isArray(data) ? data[0] : data) as unknown as Partial<VendasIaKpi> | null;
      return {
        hoje: bruto?.hoje ?? new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }),
        vendas: Array.isArray(bruto?.vendas) ? bruto!.vendas : [],
      };
    },
  });
}
